import React from 'react';
import { createPortal } from 'react-dom';
import { ASSETS } from '../assets/assetMapping';
import { mixpanelService } from '../services/mixpanelService';

/**
 * OutOfCreditsModal Component
 * 
 * A modal component that displays when users reach their rate limit.
 * Similar to PoppingModal but specifically designed for "out of credits" scenarios.
 * 
 * Usage:
 * ```tsx
 * import OutOfCreditsModal from './OutOfCreditsModal';
 * import { useOutOfCreditsModal } from '../hooks/useOutOfCreditsModal';
 * 
 * const { isOpen, openModal, closeModal, handleBuyCredits } = useOutOfCreditsModal();
 * 
 * // Show modal when rate limit is reached
 * if (rateLimitStatus?.isBlocked) {
 *   openModal();
 * }
 * 
 * <OutOfCreditsModal
 *   isOpen={isOpen}
 *   onClose={closeModal}
 *   onBuyCredits={handleBuyCredits}
 * />
 * ```
 */
interface OutOfCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  onBuyCredits?: () => void;
  isMobileView?: boolean;
}

// Mobile Modal Components
function ExpandIndicator() {
  return (
    <div className="box-border content-stretch flex flex-col gap-2.5 items-center justify-center px-[174px] py-2.5 relative size-full">
      <div className="bg-[rgba(37,54,88,0.15)] h-1 rounded-[3px] shrink-0 w-[26px]" />
    </div>
  );
}

function HomeIndicator() {
  return (
    <div className="relative size-full">
      <div className="absolute bg-black bottom-2 h-[5px] rounded-[100px] translate-x-[-50%] w-[134px]" style={{ left: "calc(50% + 0.5px)" }} />
    </div>
  );
}

const OutOfCreditsModal: React.FC<OutOfCreditsModalProps> = ({
  isOpen,
  onClose,
  title = "Out of Credits!",
  subtitle = "Looks like you've reached your limit for now. You can easily buy additional credits instantly and continue generating images right away.",
  onBuyCredits,
  isMobileView = false
}) => {
  // const [isVisible, setIsVisible] = useState(false);

  // useEffect(() => {
  //   if (isOpen) {
  //     setIsVisible(true);
  //   } else {
  //     setIsVisible(false);
  //   }
  // }, [isOpen]);

  const handleBuyCredits = () => {
    // Track convert event with credits trigger
    mixpanelService.trackConvert('credits');
    
    if (onBuyCredits) {
      onBuyCredits();
    } else {
      // Default behavior - redirect to BigVu signup
      window.open('https://desk.bigvu.tv/welcome?utm_campaign=avatar_generator&utm_content=buy_credits&utm_medium=modal&utm_source=prompt2avatar', '_blank');
    }
  };

  if (!isOpen) return null;
  // Mobile Sliding Menu View
  if (isMobileView) {
    const mobileModal = (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-end">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-[rgba(0,0,0,0.6)] backdrop-blur-[5px]"
          onClick={onClose}
        />
        
        {/* Modal Content - Sliding from bottom */}
        <div className="relative bg-white w-full rounded-tl-[15px] rounded-tr-[15px] max-h-[90vh] flex flex-col">
          {/* Top Bar with Expand Indicator */}
          <div className="flex flex-col items-start justify-start">
            <div className="w-full flex items-center justify-center py-2.5">
              <ExpandIndicator />
            </div>
          </div>

          {/* Portrait Section */}
          <div className="relative flex items-center justify-center py-4 px-4">
            <div className="relative w-[150px] h-[150px]">
              {/* Background circle */}
              <div className="absolute inset-0">
                <img alt="" className="block max-w-none size-full" src={ASSETS.ELLIPSE_39} />
              </div>
              
              {/* Portrait image */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-[120px] h-[120px] rounded-full overflow-hidden bg-gradient-to-br from-pink-400 to-purple-500">
                  <img 
                    alt="Esperanza portrait" 
                    className="w-full h-full object-cover object-center" 
                    src={ASSETS.ESPERANZA_PORTRAIT}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 overflow-y-auto px-4 pb-5">
            {/* Title */}
            <div className="text-center mb-4">
              <h2 className="font-['Inter'] font-bold text-[#253658] text-[20px] leading-normal">
                {title}
              </h2>
            </div>

            {/* Subtitle */}
            <div className="text-center mb-6">
              <p className="font-['Inter'] font-normal leading-[1.4] text-[16px] text-[rgba(37,54,88,0.8)]">
                {subtitle}
              </p>
            </div>

            {/* Credits Info */}
            <div className="bg-[rgba(255,77,91,0.08)] box-border flex gap-px items-center overflow-clip px-[12px] py-[8px] rounded-[5px] w-full mb-6">
              <div className="flex-1 flex items-center justify-between">
                <p className="flex-1 font-['Inter'] font-normal leading-[1.4] text-[#253658] text-[14px]">
                  Your AI Credits
                </p>
                <div className="flex gap-[12px] items-center">
                  <div className="relative size-[32px]">
                    <img alt="" className="block max-w-none size-full" src={ASSETS.CIRCLE_ICON} />
                  </div>
                  <div className="flex flex-col items-center justify-center text-[12px]">
                    <p className="font-['Inter'] font-medium leading-[1.4] text-[#253658] tracking-[0.12px] w-[94px]">
                      0 / 15 Credits
                    </p>
                    <p className="font-['Inter'] font-normal leading-normal text-[rgba(37,54,88,0.6)] w-[94px]">
                      15 tries spent
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-[10px] items-center">
              <button
                onClick={onClose}
                className="flex-1 box-border flex h-[40px] items-center justify-center px-[20px] py-[10.5px] rounded-[10px] hover:bg-gray-50 transition-colors"
              >
                <div className="capitalize flex flex-col font-['Inter'] font-semibold justify-center leading-[0] text-[#253658] text-[16px] text-center text-nowrap">
                  <p className="leading-normal whitespace-pre">Cancel</p>
                </div>
              </button>
              <button
                onClick={handleBuyCredits}
                className="flex-1 bg-[#4481fd] box-border flex h-[40px] items-center justify-center px-[20px] py-[10.5px] rounded-[10px] hover:bg-[#3671ed] transition-colors"
              >
                <div className="capitalize flex flex-col font-['Inter'] font-semibold justify-center leading-[0] text-[16px] text-center text-nowrap text-white">
                  <p className="leading-normal whitespace-pre">Buy credits</p>
                </div>
              </button>
            </div>
          </div>

          {/* Home Indicator */}
          <div className="h-[34px] relative w-full">
            <HomeIndicator />
          </div>
        </div>
      </div>
    );
    
    return createPortal(mobileModal, document.body);
  }

  // Desktop Modal View
  const desktopModal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Background Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content - Desktop Layout */}
      <div className={`relative bg-white rounded-[15px] w-[708px] h-[274px] overflow-hidden transition-all duration-300`}>
        <div className="flex h-full">
          {/* Left Side - Picture */}
          <div className="relative w-[278px] h-full overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute flex h-[128.873px] items-center justify-center left-[142.14px] top-[124.49px] w-[139.713px]">
              <div className="flex-none rotate-[171.078deg] scale-y-[-100%]">
                <div className="h-[111px] relative w-[124px]">
                  <div className="absolute inset-[-0.65%_-0.59%]">
                    <img alt="" className="block max-w-none size-full" src={ASSETS.VECTOR_13} />
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute flex h-[114.33px] items-center justify-center left-[-14px] top-[19.25px] w-[114.33px]">
              <div className="flex-none rotate-[45deg]">
                <div className="relative size-[80.853px]">
                  <img alt="" className="block max-w-none size-full" src={ASSETS.ELLIPSE_41} />
                </div>
              </div>
            </div>
            <div className="absolute flex h-[33.278px] items-center justify-center left-[56.02px] top-[-0.07px] w-[33.278px]">
              <div className="flex-none rotate-[45deg]">
                <div className="relative size-[23.532px]">
                  <img alt="" className="block max-w-none size-full" src={ASSETS.ELLIPSE_42} />
                </div>
              </div>
            </div>
            
            {/* Main portrait container */}
            <div className="absolute flex items-center justify-center w-full h-full">
              <div className="relative w-[200px] h-[200px]">
                {/* Background circle */}
                <div className="absolute inset-0">
                  <img alt="" className="block max-w-none size-full" src={ASSETS.ELLIPSE_39} />
                </div>
                
                {/* Portrait image with simplified masking */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-[160px] h-[160px] rounded-full overflow-hidden bg-gradient-to-br from-pink-400 to-purple-500">
                    <img 
                      alt="Esperanza portrait" 
                      className="w-full h-full object-cover object-center" 
                      src={ASSETS.ESPERANZA_PORTRAIT}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="flex-1 flex flex-col gap-[20px] items-start justify-center p-[20px]">
            <div className="flex flex-col gap-[12px] items-start w-full">
              <div className="flex gap-[16px] items-end w-full">
                <div className="flex-1 flex flex-col gap-[20px] items-start">
                  <div className="flex flex-col gap-[16px] items-start w-full">
                    <div className="flex gap-[10px] items-center w-full">
                      <div className="flex-1">
                        <div className="flex flex-col gap-[24px] items-start w-full">
                          <div className="flex items-start justify-between w-full">
                            <div className="flex-1">
                              <p className="font-['Inter:Bold',_sans-serif] font-bold text-[#253658] text-[20px] leading-normal">
                                {title}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="box-border flex gap-[4px] items-center justify-center p-[4px] rounded-[8px] size-[32px]">
                        <button
                          onClick={onClose}
                          className="flex gap-[4px] items-center justify-center p-[4px] rounded-[8px] size-full"
                        >
                          <div className="relative size-[24px]">
                            <div className="absolute left-1/2 size-[13.5px] top-1/2 translate-x-[-50%] translate-y-[-50%]">
                              <img alt="" className="block max-w-none size-full" src={ASSETS.VECTOR_64_STROKE} />
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col gap-[12px] items-start w-full">
                      <p className="font-['Inter:Regular',_sans-serif] font-normal leading-[1.4] text-[16px] text-[rgba(37,54,88,0.8)] w-full">
                        {subtitle}
                      </p>
                      <div className="flex flex-col gap-[10px] items-start w-full">
                        <div className="bg-[rgba(255,77,91,0.08)] box-border flex gap-px items-center overflow-clip px-[12px] py-[8px] rounded-[5px] w-full">
                          <div className="flex-1 flex items-center justify-between">
                            <p className="flex-1 font-['Inter:Regular',_sans-serif] font-normal leading-[1.4] text-[#253658] text-[14px]">
                              Your AI Credits
                            </p>
                            <div className="flex gap-[12px] items-center">
                              <div className="relative size-[32px]">
                                <img alt="" className="block max-w-none size-full" src={ASSETS.CIRCLE_ICON} />
                              </div>
                              <div className="flex flex-col items-center justify-center text-[12px]">
                                <p className="font-['Inter:Medium',_sans-serif] font-medium leading-[1.4] text-[#253658] tracking-[0.12px] w-[94px]">
                                  0 / 15 Credits
                                </p>
                                <p className="font-['Inter:Regular',_sans-serif] font-normal leading-normal text-[rgba(37,54,88,0.6)] w-[94px]">
                                  15 tries spent
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-[10px] items-center justify-end w-full">
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1" />
                  <div className="flex gap-[10px] items-start">
                    <div className="flex gap-[10px] items-start">
                      <button
                        onClick={onClose}
                        className="box-border flex h-[40px] items-center justify-center px-[20px] py-[10.5px] rounded-[10px] hover:bg-gray-50 transition-colors"
                      >
                        <div className="capitalize flex flex-col font-['Inter:Semi_Bold',_sans-serif] font-semibold justify-center leading-[0] text-[#253658] text-[16px] text-center text-nowrap">
                          <p className="leading-normal whitespace-pre">Cancel</p>
                        </div>
                      </button>
                      <button
                        onClick={handleBuyCredits}
                        className="bg-[#4481fd] box-border flex h-[40px] items-center justify-center px-[20px] py-[10.5px] rounded-[10px] hover:bg-[#3671ed] transition-colors"
                      >
                        <div className="capitalize flex flex-col font-['Inter:Semi_Bold',_sans-serif] font-semibold justify-center leading-[0] text-[16px] text-center text-nowrap text-white">
                          <p className="leading-normal whitespace-pre">Buy credits</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(desktopModal, document.body);
};

export default OutOfCreditsModal;
